import React, {useState} from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CalendarWeekHeader from './CalendarWeekHeader';
import CalendarWeekTimeGrid from './CalendarWeekTimeGrid';
import CalendarTimeBlock from './CalendarTimeBlock';
import useTimezone from '../../hooks/useTimezone';
import { getWeekDates, getWeeklyAppointmentStyle } from './calendarUtils';

export const CELL_WIDTH = 150;
export const HOUR_HEIGHT = 80;
export const START_HOUR = 8;
export const END_HOUR = 20;
export const HOURS_COUNT = END_HOUR - START_HOUR;

const CalendarWeekView = ({
  date,
  sessions = [],
  awayTimeBlocks = [],
  availabilityBlocks = [],
  boxStyles,
  cellWidth = CELL_WIDTH,
  hourHeight = HOUR_HEIGHT,
  startHour = START_HOUR,
  endHour = END_HOUR,
  onDateChange = (e, date) => console.log('Change date', date),
  onEditSession = (e, id) => console.log('Edit session', id),
}) => {
  const timezone = useTimezone();
  const [hideWeekends, setHideWeekends] = useState(true);
  const baseDate = date
    ? (typeof date === 'string' ? new Date(date) : date)
    : new Date();
  const startDate = moment(baseDate).startOf('week');
  const endDate = moment(baseDate).endOf('week');
  const weekDates = getWeekDates(baseDate);
  const isCurrentWeek = moment().isBetween(startDate, endDate, 'day', '[]');
  const totalWidth = cellWidth * ((hideWeekends ? 5 : 7) + 1);
  return (
    <Box
      sx={{...boxStyles, maxWidth: totalWidth}}
    >
      <Box style={{minHeight: hourHeight}}>
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between' p={2}>
          <Stack direction='row' spacing={0} alignItems='center' justifyContent='center' >
            <IconButton
              onClick={e => onDateChange(e, moment(date).subtract(1, 'week').toDate())}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={e => onDateChange(e, moment(date).add(1, 'week').toDate())}
            >
              <ChevronRight />
            </IconButton>
            {!isCurrentWeek && 
              <Button 
                variant='text' 
                onClick={e => onDateChange(e, new Date())}
              >
                Today
              </Button>
            }
          </Stack>
          <Stack direction='column' spacing={0.25} alignItems='center'>
            <Typography variant='h3' sx={{fontWeight: 'bold'}}>
              {startDate.format('MMM D')} - {endDate.format('MMM D')}
            </Typography>
            <Button 
              size='small'
              variant='text'
              onClick={e => setHideWeekends(!hideWeekends)}
            >
              {hideWeekends ? 'Show Weekends' : 'Hide Weekends'}
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ 
        overflowX: 'auto',
        // minWidth: totalWidth,
        width: '100%',
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <CalendarWeekHeader 
          cellWidth={cellWidth}
          hourHeight={hourHeight}
          weekDates={weekDates} 
          hideWeekends={hideWeekends}
        />
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <CalendarWeekTimeGrid
            cellWidth={cellWidth}
            hourHeight={hourHeight}
            hideWeekends={hideWeekends} 
            startHour={startHour}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: totalWidth,
              height: '100%',
              // pointerEvents: 'none',
            }}
          >
            {sessions
              .map(s => ({
                session: s, 
                style: s ? getWeeklyAppointmentStyle({
                  session: s, 
                  timezone,
                  weekDates, 
                  hideWeekends, 
                  cellWidth, 
                  hourHeight,
                  startHour,
                  endHour,
                }) : null
              }))
              .filter(({ session, style }) => !!session || !!style)
              .map(({ session, style }, index) => (
                <CalendarTimeBlock
                  timezone={timezone}
                  key={index}
                  session={session}
                  style={style}
                  onClick={e => onEditSession(e, session.id)}
                />
              ))
            }
            {
              awayTimeBlocks.map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`away-${index}`}
                  session={block}
                  style={getWeeklyAppointmentStyle({
                    session: block,
                    timezone,
                    weekDates,
                    hideWeekends,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                  isAwayTime={true}
                />
              )
            }
            {
              availabilityBlocks.map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`avail-${index}`}
                  session={block}
                  style={getWeeklyAppointmentStyle({
                    session: block,
                    timezone,
                    weekDates,
                    hideWeekends,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                  isAvailabilityBlock={true}
                />
              )
            }
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarWeekView;
